import { useEffect, useState } from "react";
import {
  admitChecklist,
  promote,
  recordExamResult,
  type AdmitChecklist,
} from "../api/analytics";
import { ApiError } from "../api/client";

interface Props {
  userId: number;
  userName: string;
  courseId: number;
  onClose: () => void;
  onPromoted: () => void;
}

export function AdmitDialog({ userId, userName, courseId, onClose, onPromoted }: Props) {
  const [checklist, setChecklist] = useState<AdmitChecklist | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Exam form (visible if requires_curator_exam)
  const [examPassed, setExamPassed] = useState(true);
  const [examScore, setExamScore] = useState("");
  const [examRecorded, setExamRecorded] = useState(false);

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    void admitChecklist(userId, courseId)
      .then(setChecklist)
      .catch((err) =>
        setError(err instanceof ApiError ? `Ошибка ${err.status}` : "Сеть недоступна"),
      )
      .finally(() => setLoading(false));
  }, [userId, courseId]);

  async function handleSaveExam() {
    setSubmitting(true);
    try {
      await recordExamResult(userId, {
        course_id: courseId,
        passed: examPassed,
        score: examScore ? Number(examScore) : undefined,
      });
      setExamRecorded(true);
    } catch (err) {
      alert(err instanceof ApiError ? `Ошибка ${err.status}` : "Не удалось");
    } finally {
      setSubmitting(false);
    }
  }

  async function handlePromote() {
    setSubmitting(true);
    try {
      const result = await promote(userId, {
        course_id: courseId,
        exam_score: examScore ? Number(examScore) : undefined,
      });
      window.open(`/api/academy/certificates/${result.completion_id}`, "_blank");
      onPromoted();
    } catch (err) {
      if (err instanceof ApiError && err.code === "REQUIREMENTS_NOT_MET") {
        alert("Не все требования выполнены — проверь чек-лист.");
      } else {
        alert(err instanceof ApiError ? `Ошибка ${err.status}` : "Не удалось");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 px-4">
      <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-lg">
        <header className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Допуск к работе</h2>
            <p className="text-sm text-slate-500">{userName}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
          >
            ×
          </button>
        </header>

        {loading && <div className="py-6 text-center text-slate-400">Проверяем…</div>}
        {error && (
          <div className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
        )}

        {checklist && (
          <>
            <p className="mb-3 text-sm text-slate-700">
              Курс: <strong>{checklist.course_title}</strong>
            </p>
            <ul className="mb-4 space-y-1.5 text-sm">
              <li className="flex items-center gap-2">
                <span
                  className={
                    checklist.lessons_completed === checklist.lessons_total &&
                    checklist.lessons_total > 0
                      ? "text-green-600"
                      : "text-slate-400"
                  }
                >
                  {checklist.lessons_completed === checklist.lessons_total &&
                  checklist.lessons_total > 0
                    ? "✓"
                    : "○"}
                </span>
                <span className="text-slate-700">
                  Уроки пройдены: {checklist.lessons_completed} из{" "}
                  {checklist.lessons_total}
                </span>
              </li>
              {checklist.final_quiz_required && (
                <li className="flex items-center gap-2">
                  <span
                    className={
                      checklist.final_quiz_passed ? "text-green-600" : "text-slate-400"
                    }
                  >
                    {checklist.final_quiz_passed ? "✓" : "○"}
                  </span>
                  <span className="text-slate-700">
                    Финальный тест {checklist.final_quiz_passed ? "сдан" : "не сдан"}
                  </span>
                </li>
              )}
              {checklist.requires_curator_exam && (
                <li className="flex items-center gap-2">
                  <span className={examRecorded ? "text-green-600" : "text-slate-400"}>
                    {examRecorded ? "✓" : "○"}
                  </span>
                  <span className="text-slate-700">
                    Очный экзамен{" "}
                    {examRecorded ? "зафиксирован" : "ещё не зафиксирован"}
                  </span>
                </li>
              )}
            </ul>

            {checklist.requires_curator_exam && !examRecorded && (
              <div className="mb-4 space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
                <h3 className="text-sm font-medium text-slate-900">
                  Зафиксировать экзамен
                </h3>
                <div className="flex items-center gap-3 text-sm">
                  <label className="flex items-center gap-1">
                    <input
                      type="radio"
                      checked={examPassed}
                      onChange={() => setExamPassed(true)}
                    />
                    Сдал
                  </label>
                  <label className="flex items-center gap-1">
                    <input
                      type="radio"
                      checked={!examPassed}
                      onChange={() => setExamPassed(false)}
                    />
                    Не сдал
                  </label>
                  <input
                    type="number"
                    placeholder="Балл"
                    min={0}
                    max={100}
                    value={examScore}
                    onChange={(e) => setExamScore(e.target.value)}
                    className="w-20 rounded border border-slate-300 px-2 py-1 text-sm"
                  />
                  <button
                    type="button"
                    onClick={handleSaveExam}
                    disabled={submitting}
                    className="rounded border border-slate-300 px-3 py-1 text-sm text-slate-700 hover:bg-slate-100"
                  >
                    Сохранить
                  </button>
                </div>
              </div>
            )}

            <div className="flex justify-between gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded border border-slate-300 px-4 py-1.5 text-sm text-slate-700 hover:bg-slate-100"
              >
                Закрыть
              </button>
              <button
                type="button"
                onClick={handlePromote}
                disabled={
                  submitting ||
                  !checklist.can_admit ||
                  (checklist.requires_curator_exam && !examRecorded) ||
                  (checklist.requires_curator_exam && !examPassed)
                }
                className="rounded bg-green-600 px-4 py-1.5 text-sm font-medium text-white transition hover:bg-green-700 disabled:bg-slate-300"
              >
                {submitting ? "…" : "Допустить к работе"}
              </button>
            </div>
            {!checklist.can_admit && (
              <p className="mt-3 text-xs text-slate-500">
                Кнопка станет активной, когда все обязательные пункты будут отмечены.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
